import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Domain } from "@/types/domain";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { FileText, ExternalLink, ChevronDown, ChevronUp, Image } from "lucide-react";


interface DomainElementsProps {
  domains: Domain[];
  channelId?: string;
}

const getDomainKnowledgeBases = (symbol: string, domainKBs: any[]) => {
  // Fetch real KBs from database for all domains
  if (!domainKBs) return [];
  
  return domainKBs
    .filter(kb => kb.domains?.symbol === symbol || kb.domains?.name === symbol)
    .map(kb => ({
      id: kb.id,
      title: kb.title,
      url: kb.file_url || '#',
      coverImage: kb.nft_cover_image_url,
      isExternal: false
    }));
};

const DomainElements: FC<DomainElementsProps> = ({
  domains,
  channelId
}) => {
  const navigate = useNavigate();
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);

  // Get current user
  const { data: user } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user;
    }
  });

  // Fetch all KBs with their associated domains via the junction table
  const { data: domainKBs } = useQuery({
    queryKey: ['domain-kbs', user?.id, channelId],
    queryFn: async () => {
      if (!user?.id) {
        // Not logged in - only show published KBs with their domains
        let query = supabase
          .from('knowledge_base_domains')
          .select(`
            domain_id,
            domains(name, symbol),
            knowledge_bases(
              id, title, file_url, nft_cover_image_url, status, deleted_at, creator_id, channel_id
            )
          `);
        
        if (channelId) {
          query = query.eq('knowledge_bases.channel_id', channelId);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Filter for published KBs only
        return data
          ?.filter((item: any) => 
            item.knowledge_bases?.status === 'published' && 
            item.knowledge_bases?.deleted_at === null
          )
          .map((item: any) => ({
            ...item.knowledge_bases,
            domains: item.domains
          }));
      }

      // Logged in - show published KBs + user's own KBs
      let query = supabase
        .from('knowledge_base_domains')
        .select(`
          domain_id,
          domains(name, symbol),
          knowledge_bases(
            id, title, file_url, nft_cover_image_url, status, deleted_at, creator_id, channel_id
          )
        `);
      
      if (channelId) {
        query = query.eq('knowledge_bases.channel_id', channelId);
      }
      
      const { data: kbDomainData, error } = await query;
      
      if (error) throw error;

      // Filter for published or user's own KBs
      const filteredKBs = kbDomainData
        ?.filter((item: any) => 
          item.knowledge_bases && 
          item.knowledge_bases.deleted_at === null &&
          (item.knowledge_bases.status === 'published' || item.knowledge_bases.creator_id === user.id)
        )
        .map((item: any) => ({
          ...item.knowledge_bases,
          domains: item.domains
        }));

      // Also fetch KBs user has access to via kb_access
      const { data: accessKBs, error: accessError } = await supabase
        .from('kb_access')
        .select(`
          knowledge_bases!inner(
            id, title, file_url, nft_cover_image_url, status, deleted_at, creator_id
          )
        `)
        .eq('user_id', user.id);

      if (accessError) throw accessError;

      // For access KBs, get their domains
      const accessKBIds = accessKBs?.map((item: any) => item.knowledge_bases.id) || [];
      let accessKBsWithDomains: any[] = [];
      
      if (accessKBIds.length > 0) {
        let accessQuery = supabase
          .from('knowledge_base_domains')
          .select(`
            domain_id,
            domains(name, symbol),
            knowledge_bases!inner(
              id, title, file_url, nft_cover_image_url, status, deleted_at, creator_id, channel_id
            )
          `)
          .in('knowledge_base_id', accessKBIds);
        
        if (channelId) {
          accessQuery = accessQuery.eq('knowledge_bases.channel_id', channelId);
        }
        
        const { data: accessDomainData } = await accessQuery;

        accessKBsWithDomains = accessDomainData
          ?.map((item: any) => ({
            ...item.knowledge_bases,
            domains: item.domains
          })) || [];
      }

      // Merge both lists, removing duplicates
      const allKBs = [...(filteredKBs || [])];
      const uniqueAccessKBs = accessKBsWithDomains.filter(
        (kb: any) => !allKBs.some(existing => existing.id === kb.id)
      );

      return [...allKBs, ...uniqueAccessKBs];
    },
    enabled: true
  });

  const handleDomainClick = (domain: Domain) => {
    if (expandedDomain === domain.symbol) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(domain.symbol);
    }
    if (domain.symbol === "PlTc" && expandedDomain !== "PlTc") {
      navigate("/platform-tech");
    }
  };

  const handleKnowledgeBaseClick = (kbId: string | null, url: string, isExternal: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExternal || !kbId) {
      window.open(url, "_blank");
    } else {
      navigate(`/knowledge-base/${kbId}`);
    }
  };

  const handlePdfLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open("file:///C:/Users/integ/OneDrive%20-%20ispai.io/iSPAI/iSPAI%20Corporate/iSPAI%20Content/iSPAI%20Knowledge%20Base%20NFT%20Assignment%20Agreement.pdf", "_blank");
  };

  const handleSolutionBountyPdfClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open("file:///C:/Users/integ/OneDrive%20-%20ispai.io/iSPAI/iSPAI%20Corporate/iSPAI%20Content/iSPAI%20Solution%20Bounty%20NFT%20Assignment%20Agreement_Custom.pdf", "_blank");
  };

  // Calculate KB count for each domain
  const getKBCount = (domain: Domain) => {
    const kbs = getDomainKnowledgeBases(domain.symbol, domainKBs || []);
    return kbs.length;
  };

  return <div className="grid grid-cols-5 gap-2">
      {domains.map(domain => {
        const kbCount = getKBCount(domain);
        const displayName = `${domain.name} (${kbCount})`;
        
        return <div key={domain.symbol} className="flex flex-col">
          <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-primary text-primary-foreground p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative h-20 min-w-0" onClick={() => handleDomainClick(domain)}>
                    <div className="text-sm font-bold text-center leading-tight flex-1 flex items-center justify-center px-1">{displayName}</div>
                    
                    <div className="absolute right-2 top-2">
                      {expandedDomain === domain.symbol ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-white text-black border border-gray-200 max-w-[450px] p-4">
                  <h3 className="font-bold text-lg mb-2">{domain.name}</h3>
                  
                  {domain.overview && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Overview:</p>
                      <p className="text-sm text-gray-600">{domain.overview}</p>
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Categories:</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{domain.categories}</p>
                  </div>
                  
                  {domain.keyKBs && domain.keyKBs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Key KBs:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {domain.keyKBs.map((kb, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{kb}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {domain.roleInIspai && (
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Role in iSPAI:</p>
                      <p className="text-xs text-gray-600 italic">{domain.roleInIspai}</p>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          
          {expandedDomain === domain.symbol && <div className="mt-2 bg-white rounded-lg shadow-md p-3 border border-gray-200 z-10">
              <h3 className="text-sm font-semibold mb-2">Knowledge Bases</h3>
              <div className="space-y-2">
                {getDomainKnowledgeBases(domain.symbol, domainKBs || []).map((kb, index) => <div key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs" onClick={e => handleKnowledgeBaseClick(kb.id, kb.url, kb.isExternal, e)}>
                    {kb.url.includes('.png') ? <Image className="h-3 w-3 text-primary flex-shrink-0" /> : <FileText className="h-3 w-3 text-primary flex-shrink-0" />}
                    <span className="flex-1">{kb.title}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  </div>)}
              </div>
            </div>}
        </div>;
      })}
    </div>;
};

export default DomainElements;
